import get from 'lodash.get';
import { ofType } from 'redux-observable';
import { Observable, EMPTY, concat, of } from 'rxjs';
import { mergeMap, take, catchError, switchMap } from 'rxjs/operators';

import { GuidService } from 'ui-components/services/guid-service';

import { emptyArray } from 'ui-components/default-props/empty';
import { populateSelectedReturnItems } from '../../../orders/order-details/return/populate-selected-return-items';
import {
  createOrderDetailsFulfillmentPlanPathname,
  createPathnameWithoutOrderDetails,
  isOrderDetailsPath,
} from '../../../orders/order-details/routing';
import { OrderDetailsHelperService } from '../../../services/order-details-helper-service';
import * as BatchesActions from '../../../store/batches/batches.actions';
import * as BatchApi from '../../../store/batches/batches.api';
import * as BulkActions from '../../../store/bulk/bulk.actions';
import * as CSWActions from '../../../store/csw/csw.actions';
import * as EntitiesActions from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import * as LabelsActions from '../../../store/labels/labels.actions';
import * as MarkShippedActions from '../../../store/mark-as-shipped/mark-as-shipped.actions';
import * as OrdersActions from '../../../store/orders/orders.actions';
import * as PrintActions from '../../../store/printing/print.actions';
import * as ShipmentDetailsActions from '../../../store/shipments/shipment-details/shipments.details.actions';
import { persistOrderConfiguration$ } from '../../csw/csw.observables';
import { stockLevelRequestPreFlight } from '../../inventory/inventory.actions';
import { updateOrder } from '../orders.api';
import { composeOrderDetailsNavigationParameters } from './order-navigation-resolver';
import * as Actions from './orders.details.actions';
import { orderDetailsCanvas } from '../../../orders/order-details/order-details-canvas-loader';

const NEW_SHIPMENT_CREATED_ROUTE_SUFFIX = 'new';

export const getOrderDetailsEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(
      Actions.GET_ORDER_DETAILS_REQUEST,
      ShipmentDetailsActions.SHIPMENT_DETAILS_MODAL_CLOSED,
      BatchesActions.ADDED_FULFILLMENT_PLANS_TO_CREATED_BATCH_SUCCESS,
      BatchesActions.ADD_SALES_ORDERS_TO_BATCH_SUCCESS,
      BatchesActions.ADDED_SALES_ORDERS_TO_CREATED_BATCH_SUCCESS,
    ),
    mergeMap((action) => {
      const state = getState();
      const requestLatestData = get(action, 'requestLatestData', false);
      const targetSalesOrderId = OrderDetailsHelperService.getTargetSalesOrderIdForOrderDetails(state);
      const entitiesSalesOrder = Global.selectEntitiesSalesOrderById(state, {
        salesOrderId: targetSalesOrderId,
      });
      if (entitiesSalesOrder && !requestLatestData) {
        const successAction = OrderDetailsHelperService.returnSalesOrderFromMemory(state, entitiesSalesOrder, action);
        const { fulfillmentPlanIds } = entitiesSalesOrder;
        const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds });
        return concat(
          of(successAction),
          persistOrderConfiguration$(action$, getState, successAction),
          of(Actions.silentlyGetOrderDetails(targetSalesOrderId), stockLevelRequestPreFlight({ fulfillmentPlans })),
        );
      }
      if (targetSalesOrderId && GuidService.isGuid(targetSalesOrderId)) {
        const fulfillmentPlanId = Global.selectOrderDetailsFulfillmentPlanId(state);
        return OrderDetailsHelperService.returnNewlyFetchedOrderDetails(state, targetSalesOrderId, action, fulfillmentPlanId).pipe(
          mergeMap((successAction) => {
            const fulfillmentPlanIds = Global.selectEntitiesSalesOrderProperty(state, {
              salesOrderId: targetSalesOrderId,
              property: 'fulfillmentPlanIds',
            });
            const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds });
            return concat(
              of(successAction),
              persistOrderConfiguration$(action$, getState, action),
              of(stockLevelRequestPreFlight({ fulfillmentPlans })),
            );
          }),
        );
      }
      return EMPTY;
    }),
  );
};

export const getOrderDetailsSilentlyEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SILENTLY_GET_ORDER_DETAILS),
    mergeMap((action) => {
      const state = getState();
      const targetSalesOrderId = OrderDetailsHelperService.getTargetSalesOrderIdForOrderDetails(state);
      const salesOrderId = action.salesOrderId || targetSalesOrderId;
      const fulfillmentPlanId = Global.selectOrderDetailsFulfillmentPlanId(state);
      return OrderDetailsHelperService.returnNewlyFetchedOrderDetails(state, salesOrderId, action, fulfillmentPlanId).pipe(
        mergeMap((successAction) => {
          return concat(of(successAction), persistOrderConfiguration$(action$, getState, successAction));
        }),
      );
    }),
  );
};

const getFulfillmentPlansForStockLevelRequest = ({ state, allFulfillmentPlanIdsFromSalesOrder, fulfillmentPlanId, modalId }) => {
  const fulfillmentPlanIds = modalId === orderDetailsCanvas ? [fulfillmentPlanId] : allFulfillmentPlanIdsFromSalesOrder;
  const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, {
    fulfillmentPlanIds,
  });
  return fulfillmentPlans;
};

const loadOrderDetailsModal$ = (action$, action, getState) => {
  const { salesOrderId, fulfillmentPlanId, modalId } = action;
  const state = getState();
  const entitiesSalesOrder = Global.selectEntitiesSalesOrderById(state, {
    salesOrderId,
  });
  if (entitiesSalesOrder) {
    const successAction = OrderDetailsHelperService.returnSalesOrderFromMemory(state, entitiesSalesOrder, action);
    const { fulfillmentPlanIds: allFulfillmentPlanIdsFromSalesOrder } = entitiesSalesOrder;
    const fulfillmentPlans = getFulfillmentPlansForStockLevelRequest({
      state,
      allFulfillmentPlanIdsFromSalesOrder,
      fulfillmentPlanId,
      modalId,
    });
    return concat(
      of(successAction),
      persistOrderConfiguration$(action$, getState, successAction),
      of(Actions.silentlyGetOrderDetails(salesOrderId), stockLevelRequestPreFlight({ fulfillmentPlans })),
    );
  }
  return OrderDetailsHelperService.returnNewlyFetchedOrderDetails(state, salesOrderId, action, fulfillmentPlanId).pipe(
    mergeMap((successAction) => {
      // Need to get an updated version of state here so that returnNewlyFetchedOrderDetails isn't using the previously-defined/stale state
      const updatedState = getState();
      const allFulfillmentPlanIdsFromSalesOrder = Global.selectEntitiesSalesOrderProperty(updatedState, {
        salesOrderId,
        property: 'fulfillmentPlanIds',
      });
      const fulfillmentPlans = getFulfillmentPlansForStockLevelRequest({
        state: updatedState,
        allFulfillmentPlanIdsFromSalesOrder,
        fulfillmentPlanId,
        modalId,
      });
      return concat(
        of(successAction),
        persistOrderConfiguration$(action$, getState, successAction),
        of(stockLevelRequestPreFlight({ fulfillmentPlans })),
      );
    }),
  );
};

export const openOrderDetailsModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDER_DETAILS_MODAL_OPENED),
    mergeMap(({ salesOrderId, modalId, fulfillmentPlanId, type }) => {
      const state = getState();

      const { canGoToPreviousOrder, canGoToNextOrder, previousOrderId, nextOrderId, showArrows } = composeOrderDetailsNavigationParameters(
        state,
        salesOrderId,
      );

      return Observable.create((observer) => {
        observer.next(loadOrderDetailsModal$(action$, { salesOrderId, modalId, fulfillmentPlanId, type }, getState));
        observer.next(of(Actions.setOrderGridMetaData(canGoToPreviousOrder, canGoToNextOrder, previousOrderId, nextOrderId, showArrows)));
        observer.complete();
      }).pipe(mergeMap((next$) => next$));
    }),
  );
};

// TODO: Systemize the routing here
export const closeOrderDetailsModal = (action$, { getState }, { push, history }) => {
  return action$.pipe(
    ofType(Actions.ORDER_DETAILS_MODAL_CLOSED),
    mergeMap(() => {
      const state = getState();
      const pathname = history.location.pathname;

      if (isOrderDetailsPath(pathname)) {
        const search = history.location.search;
        const correctedPathname = createPathnameWithoutOrderDetails(pathname, search);

        push({ pathname: correctedPathname, search });
      }

      if (state?.orders?.orderDetails?.isModified) {
        return concat(
          of(
            Actions.orderDetailsModalOpenedFromShipmentForReturn({
              isReturnsFromShipment: false,
            }),
          ),
          of(OrdersActions.silentRequestGetOrders()),
        );
      }
      return concat(
        of(
          Actions.orderDetailsModalOpenedFromShipmentForReturn({
            isReturnsFromShipment: false,
          }),
        ),
      );
    }),
  );
};

export const refreshGridAndOrderDetailsAfterOrderUpdate = (action$) => {
  return action$.pipe(
    ofType(
      CSWActions.CSW_CREATE_LABEL_SUCCESS,
      PrintActions.MARK_LABEL_PRINTED_SUCCESS,
      PrintActions.MARK_PACKING_SLIP_PRINTED_SUCCESS,
      PrintActions.MARK_PICK_LIST_PRINTED_SUCCESS,
      OrdersActions.MARK_PAID_SUCCESS,
      OrdersActions.UPDATE_ORDER_SUCCESS,
      LabelsActions.VOID_LABELS_SUCCESS,
      OrdersActions.SET_HOLD_UNTIL_DATE_SUCCESS,
      OrdersActions.RESTORE_ORDER_SUCCESS,
      BulkActions.BULK_END_PROCESSING_CREATE_LABEL,
      MarkShippedActions.UNDO_MARK_SHIPPED_SUCCESS,
      MarkShippedActions.MARK_SHIPPED_SUCCESS,
    ),
    mergeMap((action) => {
      const isNotFromSingleLabel = get(action, 'metadata.fromSingleLabel', false);
      const isMarkAsShippedSuccess = get(action, 'type') === MarkShippedActions.MARK_SHIPPED_SUCCESS;
      const batchIdCount = get(action, 'response.batchIds', emptyArray).length;
      const isMarkAsShippedSuccessWithoutBatchIds = isMarkAsShippedSuccess && batchIdCount === 0;

      if (isNotFromSingleLabel || isMarkAsShippedSuccessWithoutBatchIds) {
        return EMPTY;
      }

      const forceRecalulateStatuses = [CSWActions.CSW_CREATE_LABEL_SUCCESS];
      const recalculateStatuses = get(action, 'recalculateStatuses') || forceRecalulateStatuses.includes(get(action, 'type'));
      return concat(of(Actions.requestOrderDetails(recalculateStatuses)), of(Actions.markOrderAsModified()));
    }),
  );
};

// TODO: investigate the need for this more after upgrading to react-router v6. SPD-14016
export const goToOrderDetails = (action$, _, { history }) => {
  return action$.pipe(
    ofType(Actions.ORDER_DETAILS_GO_TO_ROUTE),
    mergeMap(({ salesOrderId, fulfillmentPlanId, replaceRoute }) => {
      const search = history.location.search;
      const pathname = history.location.pathname;

      const correctedPathname = createOrderDetailsFulfillmentPlanPathname(pathname, salesOrderId, fulfillmentPlanId);

      const historyAction = replaceRoute ? history.replace : history.push;

      historyAction({
        pathname: correctedPathname,
        search,
      });

      return EMPTY;
    }),
  );
};

export const setSelectedReturnItems = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SET_SELECTED_RETURN_ITEMS),
    mergeMap(({ salesOrderId, selectedItems }) => {
      const shipmentItemsForReturn = populateSelectedReturnItems({
        getState,
        salesOrderId,
        selectedItems,
      });

      return of(CSWActions.enterReturnsMode(salesOrderId, shipmentItemsForReturn));
    }),
  );
};

export const exitReturnsMode = (action$, { getState }) => {
  return action$.pipe(
    ofType(CSWActions.CSW_EXIT_RETURNS_MODE),
    mergeMap(() => {
      if (Global.selectOrderDetailsIsCreatingReturn(getState())) {
        return of(Actions.exitCreateReturn());
      }

      return EMPTY;
    }),
  );
};

export const makeCreateAnotherShipmentSucess = (action$) => {
  return action$.pipe(
    ofType(Actions.CREATE_ANOTHER_SHIPMENT_SUCCESS, Actions.ORDER_DETAILS_RESHIP_SUCCESS),
    switchMap(({ salesOrderId, newFulfillmentPlanId }) => {
      const requestOrderDetails$ = of(Actions.requestOrderDetails(true, true));
      const selectNewShipmentInOrderDetails$ = action$.pipe(
        ofType(EntitiesActions.ENTITIES_BULKLOAD),
        take(1),
        mergeMap(() =>
          of(
            Actions.goToOrderDetails({
              salesOrderId,
              fulfillmentPlanId: `${newFulfillmentPlanId}/${NEW_SHIPMENT_CREATED_ROUTE_SUFFIX}`,
              selectOrderInGrid: false,
            }),
          ),
        ),
      );
      return concat(requestOrderDetails$, selectNewShipmentInOrderDetails$);
    }),
    catchError(() => EMPTY),
  );
};

export const addSalesOrderToBatchEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.ADD_SALES_ORDER_TO_BATCH_FROM_ORDER_DETAILS),
    mergeMap(({ fulfillmentPlanSetId, salesOrderId }) => {
      return BatchApi.addSalesOrdersToBatch(fulfillmentPlanSetId, [salesOrderId]).pipe(
        mergeMap((response) => {
          const fulfillmentPlansInOtherBatches = get(response, 'data.fulfillmentPlansInOtherBatches');
          if (get(fulfillmentPlansInOtherBatches, 'length', 0) > 0) {
            return of(BatchesActions.salesOrderFulfillmentPlansBlockedFromBatch(fulfillmentPlansInOtherBatches, fulfillmentPlanSetId));
          }
          return of(BatchesActions.addSalesOrdersToBatchSuccess());
        }),
      );
    }),
  );
};

export const updateOrderItemsRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDER_DETAILS_SAVE_ORDER_ITEMS_REQUEST),
    mergeMap(({ workingOrderItems }) => {
      const state = getState();
      const salesOrderId = Global.selectOrderDetailsSalesOrderId(state);
      const salesOrder = Global.selectEntitiesSalesOrderById(state, {
        salesOrderId,
      });
      const shipToAddress =
        Global.selectEntitiesSalesOrderProperty(state, {
          salesOrderId,
          property: 'shipTos.0',
        }) || {};
      const newSalesOrder = {
        ...salesOrder,
        items: workingOrderItems,
        shipToAddress,
      };

      return updateOrder(newSalesOrder).pipe(
        mergeMap((response) => {
          const hasError = Array.isArray(response.updateErrors) && response.updateErrors.length;
          if (hasError) {
            return EMPTY;
          }
          return of(Actions.saveOrderDetailsOrderItemsSuccess(response.data));
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const updateOrderDetailsOrderItemsOnSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.ORDER_DETAILS_SAVE_ORDER_ITEMS_SUCCESS),
    mergeMap(({ response }) => {
      const { salesOrder } = response;
      const { salesOrderId } = salesOrder;
      return concat(
        of(EntitiesActions.updateSalesOrderEntityOptimistically(salesOrderId, salesOrder)),
        of(
          EntitiesActions.bulkRequestEntities({
            salesOrderIds: [salesOrderId],
          }),
        ),
        of(Actions.patchOrderDetailsOrderItems(salesOrder.items)),
        of(Actions.onFlipOrderDetailsEditOrderItemsMode()),
      );
    }),
  );
};
